import type { Booking, RecurrenceSettings } from '@/types';

/**
 * How an "all future bookings" edit of a recurring series has to be saved.
 *
 * - `details-only`: the dates, day portion, hours, resources and repeat options are as
 *   they were, so the existing occurrences are patched in place with the changed details.
 * - `schedule-changed`: the occurrences keep their pattern but move, resize or change
 *   resources, so each one is updated (and re-planned) on its own dates.
 * - `recurrence-changed`: the repeat options changed, so the existing occurrences are
 *   moved in place onto the dates the new pattern gives, keeping their ids.
 * - `recurrence-removed`: the repeat was cleared, so the rest of the series is kept as
 *   standalone bookings.
 *
 * Every kind is saved as one series update: occurrences edited on their own
 * (`recurrenceException`) keep their own values.
 */
export type SeriesEditKind =
  | 'details-only'
  | 'schedule-changed'
  | 'recurrence-changed'
  | 'recurrence-removed';

export const isSameCalendarDay = (a?: string, b?: string): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const dateA = new Date(a);
  const dateB = new Date(b);
  if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return a === b;
  return dateA.getFullYear() === dateB.getFullYear()
    && dateA.getMonth() === dateB.getMonth()
    && dateA.getDate() === dateB.getDate();
};

const normaliseDays = (days?: string[]): string =>
  (days ?? []).map(d => d.toLowerCase()).sort().join(',');

// Compares the repeat settings currently persisted for a series against the ones
// submitted from the repeat dialog. A difference means the remaining occurrences no
// longer match the pattern and have to be regenerated.
export const hasRecurrenceOptionsChanged = (
  original: RecurrenceSettings | undefined,
  updated: RecurrenceSettings | undefined
): boolean => {
  if (!original && !updated) return false;
  if (!original || !updated) return true;
  if (original.pattern !== updated.pattern) return true;
  if ((original.interval ?? 1) !== (updated.interval ?? 1)) return true;
  if (original.endType !== updated.endType) return true;
  if (updated.endType === 'on-date' && !isSameCalendarDay(original.endDate, updated.endDate)) return true;
  if (updated.endType === 'after' && (original.occurrences ?? null) !== (updated.occurrences ?? null)) return true;
  if (updated.pattern === 'weekly' && normaliseDays(original.daysOfWeek) !== normaliseDays(updated.daysOfWeek)) return true;
  if (updated.pattern === 'monthly') {
    if ((original.monthlyRecurrenceType ?? 'day-of-month') !== (updated.monthlyRecurrenceType ?? 'day-of-month')) return true;
    if (original.dayOfMonth != null && updated.dayOfMonth != null && original.dayOfMonth !== updated.dayOfMonth) return true;
    if (original.relativeWeekday?.occurrence !== updated.relativeWeekday?.occurrence) return true;
    if ((original.relativeWeekday?.weekday ?? '').toLowerCase() !== (updated.relativeWeekday?.weekday ?? '').toLowerCase()) return true;
  }
  // The series start date is only submitted when the repeat dialog was applied, and it
  // only survives on the parent occurrence's recurrence JSON, so it is compared only
  // when both sides carry it.
  if (original.startDate && updated.startDate && !isSameCalendarDay(original.startDate, updated.startDate)) return true;
  return false;
};

/**
 * Fields that place an occurrence in time or on resources, or tie it to its series and
 * tenant. A details-only patch never carries them, so every occurrence keeps its own.
 */
const NON_DETAIL_FIELDS: ReadonlySet<string> = new Set<keyof Booking>([
  'id',
  'startTime',
  'endTime',
  'durationType',
  'halfDayPeriod',
  'durationHours',
  'durationDays',
  'resourceId',
  'resourceIds',
  'resourceAssignments',
  'recurrence',
  'recurrenceGroupId',
  'isRecurrenceParent',
  'parentBookingId',
  'recurrencePending',
  'recurrenceException',
  'demandPlanGroupId',
  'demandId',
  'demandLineId',
  'source',
  'autoAdjustGroupId',
  'autoAdjustGroupStartTime',
  'autoAdjustGroupEndTime',
  'autoAdjustGroupPieceCount',
  'companyId',
  'auditHistory',
  'isFiltered',
]);

/**
 * The detail fields the batch-update endpoint applies (see `toBatchUpdateItem`). A
 * details-only patch made of these alone can be saved on the edited occurrence through
 * the same request as the ones following it.
 */
export const BATCH_UPDATE_DETAIL_FIELDS: ReadonlySet<string> = new Set<keyof Booking>([
  'status',
  'priority',
  'color',
  'projectName',
  'description',
  'billingClassification',
  'workType',
  'taskDescription',
  'bookingNotes',
  'bookingReference',
  'costCentreTask',
  'customer',
  'location',
  'addressLine2',
  'city',
  'county',
  'postcode',
  'country',
  'phone',
  'email',
  'timezone',
  'notifyReminders',
  'details',
  'requiredRole',
  'requiredSkills',
  'bookingTypeId',
  'projectId',
  'clientId',
  'costCentreId',
  'userChar',
  'userCharOptionIds',
  'userNumber',
  'userFlag',
  'userDate',
  'userNotes',
  'userTime',
]);

const isEmptyValue = (value: unknown): boolean =>
  value === undefined
  || value === null
  || value === ''
  || (Array.isArray(value) && value.length === 0)
  || (typeof value === 'object' && !(value instanceof Date) && Object.keys(value as object).length === 0);

const stableStringify = (value: unknown): string => {
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`).join(',')}}`;
  }
  return String(value);
};

/**
 * Whether two field values are the same as far as a save is concerned: ids compare as
 * strings (the form and the API disagree on number vs string), and every flavour of
 * "nothing" (undefined, null, '', [], {}) is the same nothing.
 */
export const isSameFieldValue = (a: unknown, b: unknown): boolean => {
  if (isEmptyValue(a) && isEmptyValue(b)) return true;
  if (isEmptyValue(a) || isEmptyValue(b)) return false;
  return stableStringify(a) === stableStringify(b);
};

/** Stored and submitted times differ by the precision the database keeps. */
const TIME_TOLERANCE_MS = 1000;

const isSameInstant = (a: string | undefined, b: string | undefined): boolean => {
  if (!a || !b) return !a && !b;
  const timeA = new Date(a).getTime();
  const timeB = new Date(b).getTime();
  if (isNaN(timeA) || isNaN(timeB)) return a === b;
  return Math.abs(timeA - timeB) < TIME_TOLERANCE_MS;
};

const resourceSet = (booking: Partial<Booking>): string[] => {
  const ids = new Set((booking.resourceIds ?? []).map(String));
  if (booking.resourceId != null && booking.resourceId !== '') ids.add(String(booking.resourceId));
  return [...ids].sort();
};

type ResourceAssignments = NonNullable<Booking['resourceAssignments']>;

const assignmentOf = (assignments: ResourceAssignments | undefined, resourceId: string) => {
  const assignment = assignments?.[resourceId];
  return {
    requiredRole: assignment?.requiredRole ?? '',
    requiredSkills: [...(assignment?.requiredSkills ?? [])].sort(),
  };
};

const haveAssignmentsChanged = (original: Partial<Booking>, submitted: Partial<Booking>): boolean => {
  const submittedAssignments = submitted.resourceAssignments;
  if (!submittedAssignments) return false;
  return Object.keys(submittedAssignments).some(resourceId =>
    !isSameFieldValue(
      assignmentOf(original.resourceAssignments, resourceId),
      assignmentOf(submittedAssignments, resourceId),
    ));
};

/**
 * Whether the submitted form moves, resizes or re-resources the occurrence it was opened
 * on: its start or end, its day portion (full day, first or second half, custom hours) or
 * the resources it is booked on and what each is booked for. A field the form left out
 * keeps the occurrence's own value.
 */
export const hasScheduleChanged = (original: Booking, submitted: Partial<Booking>): boolean => {
  if (submitted.startTime !== undefined && !isSameInstant(original.startTime, submitted.startTime)) return true;
  if (submitted.endTime !== undefined && !isSameInstant(original.endTime, submitted.endTime)) return true;
  if (submitted.durationType !== undefined && submitted.durationType !== original.durationType) return true;
  if ('halfDayPeriod' in submitted && !isSameFieldValue(original.halfDayPeriod, submitted.halfDayPeriod)) return true;
  if (('resourceId' in submitted || 'resourceIds' in submitted)
    && resourceSet(original).join(',') !== resourceSet({ ...original, ...pickResources(submitted) }).join(',')) {
    return true;
  }
  return haveAssignmentsChanged(original, submitted);
};

const pickResources = (submitted: Partial<Booking>): Partial<Booking> => ({
  ...('resourceId' in submitted ? { resourceId: submitted.resourceId } : {}),
  ...('resourceIds' in submitted ? { resourceIds: submitted.resourceIds } : {}),
});

/** How an "all future bookings" save of `submitted` over `original` has to be made. */
export const classifySeriesEdit = (
  original: Booking,
  submitted: Partial<Booking>,
  seriesRecurrence: RecurrenceSettings | undefined,
): SeriesEditKind => {
  if (!submitted.recurrence || submitted.recurrence.pattern === 'none') return 'recurrence-removed';
  if (hasRecurrenceOptionsChanged(seriesRecurrence, submitted.recurrence)) return 'recurrence-changed';
  if (hasScheduleChanged(original, submitted)) return 'schedule-changed';
  return 'details-only';
};

/**
 * The detail fields the form changed from the occurrence it was opened on, as a patch
 * for that occurrence and every one following it. Schedule, resource, series and tenancy
 * fields are never part of it, so each occurrence keeps its own dates and resources.
 */
export const seriesDetailChanges = (original: Booking, submitted: Partial<Booking>): Partial<Booking> => {
  const changes: Record<string, unknown> = {};
  const originalFields = original as unknown as Record<string, unknown>;
  for (const [field, value] of Object.entries(submitted)) {
    if (NON_DETAIL_FIELDS.has(field)) continue;
    if (field === 'userChar' || field === 'userCharOptionIds') continue;
    if (!isSameFieldValue(originalFields[field], value)) changes[field] = value;
  }
  const changedUserChars = new Set<number>();
  for (const key of Object.keys(submitted.userChar ?? {})) {
    const index = Number(key);
    if (submitted.userChar?.[index] !== original.userChar?.[index]) changedUserChars.add(index);
  }
  for (const key of Object.keys(submitted.userCharOptionIds ?? {})) {
    const index = Number(key);
    if (submitted.userCharOptionIds?.[index] !== original.userCharOptionIds?.[index]) changedUserChars.add(index);
  }
  if (changedUserChars.size > 0) {
    changes.userChar = Object.fromEntries([...changedUserChars].map(index =>
      [index, submitted.userChar?.[index] ?? original.userChar?.[index] ?? '']));
    changes.userCharOptionIds = Object.fromEntries([...changedUserChars]
      .filter(index => submitted.userCharOptionIds?.[index])
      .map(index => [index, submitted.userCharOptionIds![index]]));
  }
  return changes as Partial<Booking>;
};

/**
 * Whether the batch-update endpoint can apply every change in the patch. It merges only
 * the values a patch sets, so a field the edit cleared cannot travel through it.
 */
export const canApplyAsBatchPatch = (changes: Partial<Booking>): boolean =>
  Object.entries(changes).every(([field, value]) =>
    BATCH_UPDATE_DETAIL_FIELDS.has(field) && value !== undefined && value !== null);

/** An occurrence of a series as a series update reads it. */
export interface SeriesFutureOccurrence {
  id: string;
  startTime: string;
  endTime: string;
  recurrenceException?: boolean;
}

/** Where an "all future bookings" edit puts the occurrences following the edited one. */
export interface SeriesFutureSchedule {
  fromStartTime: string;
  excludeIds: readonly string[];
  originalStartTime?: string;
  originalEndTime?: string;
  newStartTime?: string;
  newEndTime?: string;
  /** The start of every occurrence after the edited one; occurrences move onto them in order. */
  occurrenceStartTimes?: readonly string[];
}

export interface SeriesFuturePlan {
  /** Each occurrence updated, with its new times when they change. */
  moves: Array<{ id: string; startTime?: string; endTime?: string }>;
  /** Occurrences past the last new date. */
  removedIds: string[];
  /** Occurrences edited on their own, left as they are. */
  skippedIds: string[];
  /** New dates past the last occurrence, to be created. */
  unmatchedStartTimes: string[];
}

const toLocalIso = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

/**
 * The occurrences an "all future bookings" edit updates and where it puts them, as the
 * server's series update does: with new dates the first following occurrence moves onto
 * the first date and so on, otherwise each keeps its date and takes the edited booking's
 * new time of day and length.
 */
export const planSeriesFutureUpdate = (
  occurrences: readonly SeriesFutureOccurrence[],
  schedule: SeriesFutureSchedule,
): SeriesFuturePlan => {
  const from = new Date(schedule.fromStartTime).getTime();
  const excluded = new Set(schedule.excludeIds.map(String));
  const following = occurrences
    .filter(occurrence => !excluded.has(String(occurrence.id)) && new Date(occurrence.startTime).getTime() >= from)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const skippedIds = following.filter(o => o.recurrenceException).map(o => String(o.id));
  const eligible = following.filter(o => !o.recurrenceException);
  const plan: SeriesFuturePlan = { moves: [], removedIds: [], skippedIds, unmatchedStartTimes: [] };

  const newStart = schedule.newStartTime ? new Date(schedule.newStartTime) : undefined;
  const newEnd = schedule.newEndTime ? new Date(schedule.newEndTime) : undefined;
  const newDuration = newStart && newEnd ? newEnd.getTime() - newStart.getTime() : undefined;

  if (schedule.occurrenceStartTimes && newDuration !== undefined) {
    const dates = [...new Set(schedule.occurrenceStartTimes)]
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    eligible.forEach((occurrence, index) => {
      if (index >= dates.length) {
        plan.removedIds.push(String(occurrence.id));
        return;
      }
      const start = new Date(dates[index]);
      plan.moves.push({
        id: String(occurrence.id),
        startTime: toLocalIso(start),
        endTime: toLocalIso(new Date(start.getTime() + newDuration)),
      });
    });
    plan.unmatchedStartTimes = dates.slice(eligible.length);
    return plan;
  }

  const originalStart = schedule.originalStartTime ? new Date(schedule.originalStartTime) : undefined;
  const originalEnd = schedule.originalEndTime ? new Date(schedule.originalEndTime) : undefined;
  const startMoved = !!newStart && !!originalStart && newStart.getTime() !== originalStart.getTime();
  const resized = newDuration !== undefined && !!originalStart && !!originalEnd
    && newDuration !== originalEnd.getTime() - originalStart.getTime();

  for (const occurrence of eligible) {
    if ((!startMoved && !resized) || !newStart || newDuration === undefined) {
      plan.moves.push({ id: String(occurrence.id) });
      continue;
    }
    const start = new Date(occurrence.startTime);
    if (startMoved) {
      start.setHours(newStart.getHours(), newStart.getMinutes(), newStart.getSeconds(), 0);
    }
    plan.moves.push({
      id: String(occurrence.id),
      startTime: toLocalIso(start),
      endTime: toLocalIso(new Date(start.getTime() + newDuration)),
    });
  }
  return plan;
};

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const WEEK_ORDINALS = ['first', 'second', 'third', 'fourth'] as const;

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const formatLocalDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`;
};

/**
 * The repeat options a series takes when one of its occurrences is moved by hand to
 * another day: the repeat day moves with it, as a repeat booking created on that day
 * would repeat on it. Weekly days shift by the days moved, a monthly day number becomes
 * the new day and a monthly weekday becomes the new day's place in its month. A series
 * start after the new day moves back to it, so the moved occurrence is in the series.
 */
export const shiftRecurrenceWithStart = (
  recurrence: RecurrenceSettings,
  originalStartTime: string,
  newStartTime: string,
): RecurrenceSettings => {
  const from = startOfDay(new Date(originalStartTime));
  const to = startOfDay(new Date(newStartTime));
  if (isNaN(from.getTime()) || isNaN(to.getTime()) || from.getTime() === to.getTime()) return recurrence;

  const shifted: RecurrenceSettings = { ...recurrence };
  if (recurrence.pattern === 'weekly' && recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0) {
    const offset = (to.getDay() - from.getDay() + 7) % 7;
    const days = recurrence.daysOfWeek
      .map(day => WEEKDAYS.indexOf(day.toLowerCase() as typeof WEEKDAYS[number]))
      .filter(index => index >= 0)
      .map(index => WEEKDAYS[(index + offset) % 7]);
    shifted.daysOfWeek = [...new Set(days)];
  } else if (recurrence.pattern === 'monthly') {
    if (recurrence.monthlyRecurrenceType === 'relative-weekday' && recurrence.relativeWeekday) {
      const day = to.getDate();
      const daysInMonth = new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();
      const weekOfMonth = Math.ceil(day / 7);
      const isLastWeek = day + 7 > daysInMonth;
      const weekdayName = WEEKDAYS[to.getDay()];
      shifted.relativeWeekday = {
        occurrence: (recurrence.relativeWeekday.occurrence === 'last' && isLastWeek) || weekOfMonth > 4
          ? 'last'
          : WEEK_ORDINALS[weekOfMonth - 1],
        weekday: weekdayName.charAt(0).toUpperCase() + weekdayName.slice(1),
      };
    } else if (recurrence.dayOfMonth != null) {
      shifted.dayOfMonth = to.getDate();
    }
  }
  if (recurrence.startDate) {
    const seriesStart = startOfDay(new Date(recurrence.startDate));
    if (!isNaN(seriesStart.getTime()) && seriesStart > to) shifted.startDate = formatLocalDate(to);
  }
  return shifted;
};

const withSeriesPatch = (booking: Booking, patch: Partial<Booking>): Booking => ({
  ...booking,
  ...patch,
  ...(patch.userChar ? {
    userChar: { ...booking.userChar, ...patch.userChar },
    userCharOptionIds: {
      ...Object.fromEntries(Object.entries(booking.userCharOptionIds ?? {})
        .filter(([index]) => !(index in patch.userChar!))),
      ...patch.userCharOptionIds,
    },
  } : {}),
  id: booking.id,
  resourceId: booking.resourceId,
  resourceIds: booking.resourceIds,
});

/**
 * The bookings with the changed details shown on the given occurrences straight away. An
 * occurrence the list does not hold yet (one the planner loaded for its date range) is
 * added, patched, so the planner shows it changed too.
 */
export const patchSeriesOccurrences = (
  prev: readonly Booking[],
  occurrences: ReadonlyMap<string, Booking>,
  patch: Partial<Booking>,
): Booking[] => {
  const known = new Set(prev.map(booking => String(booking.id)));
  const kept = prev.map(booking => {
    const id = String(booking.id);
    return occurrences.has(id) ? withSeriesPatch(booking, patch) : booking;
  });
  const added = [...occurrences.entries()]
    .filter(([id]) => !known.has(id))
    .map(([, booking]) => withSeriesPatch(booking, patch));
  return added.length > 0 ? [...kept, ...added] : kept;
};

/**
 * The bookings with the server's copies of the saved ones merged in and the removed ones
 * left out. The server's copy names the resources a booking is held by only when they
 * changed, so otherwise they carry over from the copy already held, or, for a booking not
 * held yet, from its copy on screen: a booking held by many resources stays on all of them.
 */
export const mergeSavedSeriesBookings = (
  prev: readonly Booking[],
  saved: ReadonlyMap<string, Booking>,
  removed: ReadonlySet<string>,
  onScreen: ReadonlyMap<string, Booking> = new Map(),
): Booking[] => {
  const merge = (shown: Booking | undefined, server: Booking): Booking => ({
    ...shown,
    ...server,
    resourceIds: server.resourceIds ?? shown?.resourceIds,
    resourceAssignments: server.resourceAssignments ?? shown?.resourceAssignments,
    auditHistory: shown?.auditHistory ?? server.auditHistory,
  });
  const known = new Set(prev.map(booking => String(booking.id)));
  const kept = prev
    .filter(booking => !removed.has(String(booking.id)))
    .map(booking => {
      const server = saved.get(String(booking.id));
      return server ? merge(booking, server) : booking;
    });
  const added = [...saved.entries()]
    .filter(([id]) => !known.has(id) && !removed.has(id))
    .map(([id, server]) => merge(onScreen.get(id), server));
  return added.length > 0 ? [...kept, ...added] : kept;
};

/**
 * The bookings with the given patched occurrences put back as they were before the patch:
 * a held one gets its earlier copy back, and one added only to show the patch is left out,
 * so the planner shows its copy as loaded.
 */
export const revertSeriesPatch = (
  prev: readonly Booking[],
  patchedIds: ReadonlySet<string>,
  held: ReadonlyMap<string, Booking>,
): Booking[] => prev
  .filter(booking => !patchedIds.has(String(booking.id)) || held.has(String(booking.id)))
  .map(booking => (patchedIds.has(String(booking.id)) ? held.get(String(booking.id)) ?? booking : booking));
